/* eslint-disable @typescript-eslint/no-explicit-any */

/** Shape sent to the TIP /messages endpoint as `ui_context` */
export interface UIContext {
  type: string;
  data: Record<string, any>;
  state_timestamp: string; // ISO 8601 – captured at the UI event time, NOT request time
}

// PII / internal-tracking field names to strip from context data
const SENSITIVE_FIELD_PATTERNS = [
  'user_name',
  'full_name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'passport',
  'drivers_license',
  'ssn',
  'date_of_birth',
  'credit_card',
  'card_number',
  'cvv',
  'payment_method',
  'password',
  'api_key',
  'access_token',
  'session_token',
  'user_id',
  'account_id',
  'loyalty_number',
  'search_history',
  'booking_history',
  'analytics_session_id',
  'tracking_id',
  'internal_user_id',
  'ab_test',
  'cache_ttl',
  'data_source',
];

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_FIELD_PATTERNS.some(pattern => lower.includes(pattern));
}

function filterSensitiveData(data: any): any {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) {
    return data.map(item => (typeof item === 'object' ? filterSensitiveData(item) : item));
  }
  const filtered: Record<string, any> = {};
  for (const [key, value] of Object.entries(data)) {
    if (isSensitiveKey(key)) continue;
    filtered[key] = typeof value === 'object' && value !== null ? filterSensitiveData(value) : value;
  }
  return filtered;
}

const STATE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Lazy import to avoid circular dependency
let _store: any = null;
function getStore() {
  if (!_store) {
    // Dynamic import to break circular dependency
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    _store = require('../store').useMarriottAIPreviewStore;
  }
  return _store;
}

/**
 * Build displayed_results array from the current navigation view data.
 * Returns null if no valid hotel data is available.
 */
function buildDisplayedResultsFromStore(): Array<{
  position: number;
  property_id: string;
  property_name: string;
}> | null {
  try {
    const state = getStore().getState();
    const currentNav = state.navigationStack?.[state.navigationStack.length - 1];
    const edges =
      currentNav?.data?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges;

    if (!Array.isArray(edges) || edges.length === 0) {
      return null;
    }

    return edges.map((edge: any, index: number) => {
      const node = edge?.node;
      const property = node?.property;
      const basicInfo = property?.basicInformation;
      return {
        position: index + 1,
        property_id: property?.id ?? '',
        property_name: basicInfo?.name ?? '',
      };
    });
  } catch {
    return null;
  }
}

class UIContextManager {
  private currentContext: UIContext | null = null;

  /**
   * Stores the last search-related context separately.
   * This allows us to restore it when HQV panel closes.
   */
  private lastSearchContext: UIContext | null = null;

  /**
   * Tracks whether the HQV (Hotel Quick View) panel is currently open.
   * Only send property_details_view context when this is true.
   */
  private isHqvPanelOpen = false;

  /**
   * Get the current chat ID from the store.
   */
  private getCurrentChatId(): string | null {
    try {
      return getStore().getState().currentChatId;
    } catch {
      return null;
    }
  }

  /**
   * Set the current UI context.  PII is automatically stripped.
   * @param type  Context type e.g. "property_details_view" | "search_results_sorted"
   * @param data  Raw payload – sensitive fields are filtered before storing
   */
  setState(type: string, data: Record<string, any>): void {
    const newContext: UIContext = {
      type,
      data: filterSensitiveData(data),
      state_timestamp: new Date().toISOString(), // event time, not request time
    };

    this.currentContext = newContext;

    // Also store search-related context separately so we can restore it after HQV closes
    if (type === 'search_results_filtered' || type === 'search_results_sorted') {
      this.lastSearchContext = newContext;
    }
  }

  /** Explicitly clear the context (e.g. on navigation away, new chat, etc.) */
  clearState(): void {
    this.currentContext = null;
  }

  /**
   * Clear only the HQV context and restore search context if available.
   * Called when HQV panel closes.
   */
  clearHqvContext(): void {
    // If current context is property_details_view, restore the last search context
    if (this.currentContext?.type === 'property_details_view') {
      this.currentContext = this.lastSearchContext;
    }
  }

  /**
   * Clear all context including search context.
   * Called on new chat creation.
   * Note: Does NOT clear the per-conversation search modified flag - that's stored in the store.
   */
  clearAllContext(): void {
    this.currentContext = null;
    this.lastSearchContext = null;
  }

  /**
   * Mark that user has explicitly modified search parameters for the current conversation.
   * Called when NLS FE endpoint returns results (user changed mini search or filters).
   * @param chatId Optional chat ID, defaults to current chat
   */
  markSearchModified(chatId?: string): void {
    const targetChatId = chatId ?? this.getCurrentChatId();
    if (targetChatId) {
      try {
        getStore().getState().setConversationSearchModified(targetChatId, true);
      } catch {
        // Ignore if store not available
      }
    }
  }

  /**
   * Clear the search modified flag for the current conversation.
   * Called when TIP returns new search results (hotel_search_completed).
   * @param chatId Optional chat ID, defaults to current chat
   */
  clearSearchModifiedFlag(chatId?: string): void {
    const targetChatId = chatId ?? this.getCurrentChatId();
    if (targetChatId) {
      try {
        getStore().getState().setConversationSearchModified(targetChatId, false);
      } catch {
        // Ignore if store not available
      }
    }
  }

  /**
   * Check if user has modified search for the current conversation.
   * @param chatId Optional chat ID, defaults to current chat
   */
  hasSearchBeenModified(chatId?: string): boolean {
    const targetChatId = chatId ?? this.getCurrentChatId();
    if (targetChatId) {
      try {
        return getStore().getState().getConversationSearchModified(targetChatId);
      } catch {
        return false;
      }
    }
    return false;
  }

  /**
   * Set HQV panel open state.
   * @param isOpen Whether the HQV panel is currently open
   */
  setHqvPanelOpen(isOpen: boolean): void {
    this.isHqvPanelOpen = isOpen;
  }

  /** Check if HQV panel is open */
  isHqvOpen(): boolean {
    return this.isHqvPanelOpen;
  }

  /**
   * Returns the current context for attaching to the next /message request.
   * Only returns context when:
   * 1. HQV panel is open (property_details_view), OR
   * 2. User has explicitly modified search (search_results_filtered/sorted)
   *
   * Returns `null` if:
   * - No context has been set AND user hasn't modified search
   * - Context has expired (> 5 min)
   * - Context type is search-related but user hasn't modified search
   * - Context type is property_details_view but HQV panel is closed
   */
  getStateForMessage(): UIContext | null {
    // If no current context but user has modified search (e.g., after conversation switch),
    // create a search context with displayed_results from the current view
    if (!this.currentContext) {
      if (this.hasSearchBeenModified()) {
        // Build displayed_results from the current navigation view data
        const displayedResults = buildDisplayedResultsFromStore();
        return {
          type: 'search_results_filtered',
          data: {
            user_modified: true,
            ...(displayedResults && { displayed_results: displayedResults }),
          },
          state_timestamp: new Date().toISOString(),
        };
      }
      return null;
    }

    const age = Date.now() - new Date(this.currentContext.state_timestamp).getTime();
    if (age > STATE_EXPIRY_MS) {
      this.clearState();
      // Even if context expired, check if search was modified
      if (this.hasSearchBeenModified()) {
        // Build displayed_results from the current navigation view data
        const displayedResults = buildDisplayedResultsFromStore();
        return {
          type: 'search_results_filtered',
          data: {
            user_modified: true,
            ...(displayedResults && { displayed_results: displayedResults }),
          },
          state_timestamp: new Date().toISOString(),
        };
      }
      return null;
    }

    const { type } = this.currentContext;

    // For property_details_view, only send if HQV panel is open
    if (type === 'property_details_view') {
      if (!this.isHqvPanelOpen) {
        return null;
      }
      return this.currentContext;
    }

    // For search-related context, only send if user has explicitly modified search
    if (type === 'search_results_filtered' || type === 'search_results_sorted') {
      if (!this.hasSearchBeenModified()) {
        return null;
      }
      return this.currentContext;
    }

    // For any other context types, return as-is
    return this.currentContext;
  }

  /**
   * Consume context for message - returns context for sending to TIP.
   * NOTE: Does NOT clear the search modified flag. The flag should only be cleared
   * when TIP returns new search results (via clearSearchModifiedFlag()).
   */
  consumeContextForMessage(): UIContext | null {
    return this.getStateForMessage();
  }

  /** Human-readable summary of the current context (useful for debug UI) */
  getStateDescription(): string {
    if (!this.currentContext) return 'No UI context';
    const { type, data } = this.currentContext;
    const modifiedFlag = this.hasSearchBeenModified() ? ' [user-modified]' : '';
    const hqvFlag = this.isHqvPanelOpen ? ' [HQV open]' : '';
    switch (type) {
      case 'property_details_view':
        return `Viewing: ${data['property_name'] ?? data['marshaCode'] ?? 'hotel'}${hqvFlag}`;
      case 'search_results_sorted':
        return `Results sorted by: ${data['sort_by'] ?? 'default'}${modifiedFlag}`;
      case 'search_results_filtered':
        return `Results filtered: ${data['total_results'] ?? '?'} shown${modifiedFlag}`;
      default:
        return `Context: ${type}`;
    }
  }
}

/** Singleton instance – import this directly from anywhere in the app */
export const uiContextManager = new UIContextManager();
